import React, { useState } from "react";
import { Session, sessionService } from "../services/session.service";
import { Button, message, Table, Tag, theme } from "antd";
import AddSessionsModal from "../components/AddSessionsModal";
import { ReloadOutlined } from "@ant-design/icons";
import { useSessions } from "../hooks/useSessions";

const SessionManagement: React.FC = () => {
  const {
    token: { colorBgContainer, colorBorderSecondary },
  } = theme.useToken();

  const [deleteIdsLoading, setDeleteIdsLoading] = useState<
    Record<string, boolean>
  >({});

  const { data, isLoading, refetch, isRefetching } = useSessions();

  const handleDelete = async (id: string) => {
    try {
      setDeleteIdsLoading((prev) => ({ ...prev, [id]: true }));
      await sessionService.deleteSession(id);
      message.success("Session deleted successfully");
      refetch();
    } catch (error) {
      message.error("Failed to delete session");
    } finally {
      delete deleteIdsLoading[id];
    }
  };

  const columns = [
    {
      title: "",
      dataIndex: "index",
      key: "username",
      render: (value: string, record: Session, index: number) => index + 1,
    },
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      render: (value: string) => (
        <a
          href={`https://x.com/${value}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
        </a>
      ),
    },
    {
      title: "Status",
      dataIndex: "isLimited",
      key: "isLimited",
      render: (isLimited: boolean) => (
        <span style={{ color: isLimited ? "red" : "green" }}>
          {isLimited ? (
            <Tag color="red">Limited</Tag>
          ) : (
            <Tag color="green">Active</Tag>
          )}
        </span>
      ),
      sorter: (a: Session, b: Session) =>
        (a.limited ? 1 : 0) - (b.limited ? 1 : 0),
    },
    {
      title: "Limit In",
      dataIndex: "limitedAt",
      key: "limitedAt",
      render: (limitedAt: string, record: Session) => {
        if (!limitedAt) return "No limit";
        const limitDate = new Date(limitedAt);
        const now = new Date();
        const diffInMinutes = Math.floor(
          (limitDate.getTime() + 60 * 60 * 1000 - now.getTime()) / (1000 * 60)
        );
        return `${diffInMinutes} minutes remaining`;
      },
    },
    {
      title: "API Limits",
      dataIndex: "apis",
      key: "apis",
      render: (apis: Session["apis"]) => {
        const limits = Object.entries(apis);
        if (limits.length === 0) return "No limits";
        return limits.map(([endpoint, limit]) => (
          <div key={endpoint} style={{ fontSize: "12px" }}>
            {endpoint?.split("/").pop()}: {limit.remaining} remaining
          </div>
        ));
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: Session) => (
        <Button
          danger
          onClick={() => handleDelete(record.username)}
          loading={deleteIdsLoading[record.username]}
          size="small"
          type="text"
        >
          Delete
        </Button>
      ),
    },
  ];

  return (
    <div className="relative">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          position: "sticky",
          top: 0,
          zIndex: 100,
          background: colorBgContainer,
          borderBottom: `1px solid ${colorBorderSecondary}`,
        }}
      >
        <h2>Session Management</h2>
        <AddSessionsModal onSuccess={() => refetch()} />
      </div>

      <div
        style={{
          padding: "16px 0",
          display: "flex",
          alignItems: "center",
          gap: 8,
        }}
      >
        <span>
          <Tag color="blue">{data?.length || 0} sessions</Tag>
          <Tag color="green">
            {data?.filter((session) => !session.limited).length || 0} active
          </Tag>
          <Tag color="red">
            {data?.filter((session) => session.limited).length || 0} limited
          </Tag>
        </span>
        <Button
          type="default"
          size="small"
          onClick={() => refetch()}
          loading={isRefetching}
          icon={<ReloadOutlined style={{ fontSize: 12, marginBottom: 2 }} />}
        >
          Refresh
        </Button>
      </div>

      <Table
        rowKey="username"
        dataSource={data}
        columns={columns}
        loading={isLoading}
        pagination={false}
      />
    </div>
  );
};

export default SessionManagement;
